
import React, { useState } from "react";

import { toast } from "../../../../utils/toast";
import { uploadMediaFile } from "../../../../services/api";
import { getErrorMessage } from "../../../../utils/utilMethods";
import type { FieldDefinition } from "../../../../types/types";
import type { TranslationFn } from "../dealFormFields";

type UploadedFileValue =
  | string
  | { fileUrl?: string; url?: string; fileName?: string; name?: string }
  | null
  | undefined;

const getUploadedFileUrl = (value: UploadedFileValue): string => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  return value.fileUrl || value.url || "";
};

const getUploadedFileName = (value: UploadedFileValue, fallbackName = ""): string => {
  if (fallbackName) return fallbackName;
  if (value && typeof value === "object") {
    const name = value.fileName || value.name;
    if (name) return String(name);
  }

  const fileUrl = getUploadedFileUrl(value);
  if (!fileUrl) return "";

  try {
    const url = new URL(fileUrl);
    return decodeURIComponent(
      url.pathname.split("/").filter(Boolean).pop() || "",
    );
  } catch {
    return decodeURIComponent(fileUrl.split("/").pop()?.split("?")[0] || "");
  }
};

const getAcceptedExtensions = (accept?: string): string[] =>
  String(accept || "")
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part.startsWith("."));

interface FileUploadFieldProps {
  field: FieldDefinition;
  value: UploadedFileValue;
  isInvalid?: boolean;
  tr: TranslationFn;
  onChange: (val: UploadedFileValue) => void;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  field,
  value,
  isInvalid,
  tr,
  onChange,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");

  const fileUrl = getUploadedFileUrl(value);
  const fileName = getUploadedFileName(value, uploadedFileName);
  const validation = field.validation;
  const maxSizeMB = Number(validation?.maxSizeMB || 0);
  const allowedTypes = validation?.allowedTypes ?? [];
  const acceptedExtensions = getAcceptedExtensions(field.accept);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (maxSizeMB > 0 && file.size > maxSizeMB * 1024 * 1024) {
      toast.error(
        tr("fileSizeLimit", {
          size: maxSizeMB,
          defaultValue: `File size must be ${maxSizeMB}MB or less`,
        }),
      );
      return;
    }

    const fileExtension = `.${file.name.split(".").pop()?.toLowerCase() || ""}`;
    const typeAllowed =
      allowedTypes.length === 0 ||
      allowedTypes.includes(file.type) ||
      acceptedExtensions.includes(fileExtension);

    if (!typeAllowed) {
      toast.error(
        tr("unsupportedFileType", {
          defaultValue: "Please upload a supported file type",
        }),
      );
      return;
    }

    setIsUploading(true);
    setUploadedFileName(file.name);

    try {
      const uploadedUrl = await uploadMediaFile(file);
      const storedValue =
        field.storageType === "link"
          ? uploadedUrl
          : {
              fileName: file.name,
              fileUrl: uploadedUrl,
              fileType: file.type,
              fileSize: file.size,
            };

      onChange(storedValue);
    } catch (error: unknown) {
      setUploadedFileName("");
      toast.error(
        getErrorMessage(
          error,
          tr("fileUploadFailed", { defaultValue: "File upload failed" }),
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label
        className={[
          "flex h-10 w-full items-center overflow-hidden rounded-lg border bg-white text-[13px] transition-all duration-150",
          isUploading
            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
            : isInvalid
              ? "cursor-pointer border-red-400 text-slate-800 focus-within:ring-2 focus-within:ring-red-100"
              : "cursor-pointer border-slate-200 text-slate-800 hover:border-slate-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100",
        ].join(" ")}
      >
        <span className="flex h-full flex-shrink-0 items-center border-r border-slate-200 bg-blue-50 px-3 font-semibold text-blue-700">
          {tr("chooseFile", { defaultValue: "Choose file" })}
        </span>
        <span
          className={[
            "min-w-0 flex-1 truncate px-3",
            fileName ? "text-slate-700" : "text-slate-400",
          ].join(" ")}
          title={fileName || tr("noFileChosen", { defaultValue: "No file chosen" })}
        >
          {fileName || tr("noFileChosen", { defaultValue: "No file chosen" })}
        </span>
        <input
          className="sr-only"
          type="file"
          accept={field.accept}
          disabled={isUploading}
          onChange={handleFileChange}
        />
      </label>

      <div className="min-h-[18px]">
        {isUploading ? (
          <p className="text-[11px] font-medium text-blue-500">
            {tr("uploadingFile", { defaultValue: "Uploading..." })}
          </p>
        ) : fileUrl ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5">
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 truncate text-[12px] font-medium text-blue-600 hover:text-blue-700"
              title={fileName || fileUrl}
            >
              {fileName ||
                tr("viewUploadedFile", { defaultValue: "View uploaded file" })}
            </a>
            <button
              type="button"
              onClick={() => {
                setUploadedFileName("");
                onChange(undefined);
              }}
              className="flex-shrink-0 text-[11px] font-semibold text-red-500 hover:text-red-600"
            >
              {tr("removeFile", { defaultValue: "Remove" })}
            </button>
          </div>
        ) : field.placeholder ? (
          <p className="truncate text-[11px] text-slate-400" title={tr(field.placeholder)}>
            {tr(field.placeholder)}
          </p>
        ) : null}
      </div>
    </div>
  );
};
